import { z } from 'zod';
import { uuidV7Schema } from '../uuid.js';
/** Une journée pleine, en minutes : l'unité des durées de séminaire (#576). */
export const MINUTES_PER_DAY = 24 * 60;
/**
 * Le plafond d'une durée de point interne — **deux semaines**, le plus long séminaire que GHP a
 * nommé (#576, point du 21/10/2026 : « la semaine ou plus »).
 *
 * ⚠️ **Une borne d'intégrité, pas une règle métier** (première exception de M21) : au-delà, le
 * point déborderait la fenêtre que la grille et la tablette lisent (`PLANNING_WINDOW_MAX_DAYS`).
 * Il valait une journée jusqu'à #576, quand la grille ne savait peindre un point que sur son
 * premier jour.
 */
export const INTERNAL_MEETING_MAX_DURATION_MINUTES = 14 * MINUTES_PER_DAY;
/**
 * Les durées que la modale **propose** : celles du prototype validé (`internalModal`,
 * `mocks/ghp-bo/screens.js`) — 30 min, 1 h, 1 h 30, 2 h —, puis les journées entières d'un
 * séminaire, de un à quatorze jours (#576).
 *
 * ⚠️ **Ce n'est pas la contrainte du modèle**, qui accepte tout entier positif jusqu'au plafond
 * ci-dessus. Les figer dans le schéma refuserait une réunion de 45 minutes qu'aucune règle métier
 * n'interdit, ce qui sortirait des trois exceptions de M21. L'écran propose, la base admet plus.
 */
export const INTERNAL_MEETING_DURATIONS = [
    30,
    60,
    90,
    120,
    ...Array.from({ length: 14 }, (_, index) => (index + 1) * MINUTES_PER_DAY),
];
/**
 * La durée pré-sélectionnée à l'ouverture de la modale — une heure, la valeur que le prototype
 * pose sur ses deux points internes de démonstration (`internalPoints`, `mocks/ghp-bo/data.js`).
 */
export const INTERNAL_MEETING_DEFAULT_DURATION = 60;
/**
 * Une durée de créneau, telle qu'elle s'écrit à l'écran : « 30 min », « 1 h », « 1 h 30 », « 2 h ».
 *
 * ⚠️ **Dans le domaine, parce que trois endroits l'écrivent et deux apps** : la liste de durées que
 * la modale du bureau propose, la carte de la grille du planning qui relit ce qui a été enregistré,
 * et la carte de l'agenda de la tablette (#319). Deux mises en forme du même nombre finiraient par
 * annoncer deux durées pour un même point — au bureau l'une, à l'équipe l'autre.
 *
 * ⚠️ **Les libellés ne sont pas repris verbatim du prototype**, et c'est délibéré. Sa fonction —
 * `d < 60 ? d + ' min' : (d / 60) + ' h' + (d % 60 ? '30' : '')` (`internalModal`,
 * `mocks/ghp-bo/screens.js`) — rend « 1.5 h30 » pour 90 minutes : c'est un défaut de la maquette,
 * pas une convention à porter. Les trois autres valeurs sont écrites comme elle les écrit.
 */
export function durationLabel(minutes) {
    if (minutes >= MINUTES_PER_DAY && minutes % MINUTES_PER_DAY === 0) {
        const days = minutes / MINUTES_PER_DAY;
        return `${String(days)}${NBSP}${days > 1 ? 'jours' : 'jour'}`;
    }
    if (minutes < 60)
        return `${String(minutes)}${NBSP}min`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest === 0 ? `${String(hours)}${NBSP}h` : `${String(hours)}${NBSP}h${NBSP}${String(rest)}`;
}
// Espaces insécables : sur une carte étroite de la grille, « 1 h » se coupait en « 1 » / « h ».
const NBSP = '\u00a0';
/**
 * La durée d'un point interne, en minutes — **entière et positive**, plafonnée à la journée.
 *
 * Posée une fois et partagée par les trois schémas : la ligne en base, ce qu'un opérateur envoie
 * et ce qui revient sur le fil portent la **même** contrainte, sans qu'une copie puisse dériver.
 */
const durationMinutesSchema = z.int().positive().max(INTERNAL_MEETING_MAX_DURATION_MINUTES);
/**
 * `InternalMeeting` — le **point interne** d'une équipe : réunion, brief, passage à l'atelier.
 * Il occupe un créneau du planning sans être un contrôle.
 *
 * ⚠️ **Une table à part, et non une nature de `Appointment`.** Un rendez-vous est le lien entre
 * une demande et un usager : sa clé étrangère vers `inquiry` est `notNull`, la confirmation
 * e-mail part de son contact, et les prédicats de [M3] testent sa présence pour dire qu'une
 * demande est planifiée. Un point interne n'a **ni demande, ni usager, ni conclusion** — le
 * loger dans `appointment` obligerait à rendre `inquiry_id` nullable, et chacun de ces trois
 * usages devrait alors exclure les lignes sans demande. Le premier qui l'oublierait ferait
 * apparaître un brief d'équipe dans la tournée d'un agent, ou compterait une demande comme
 * planifiée parce que son équipe a une réunion.
 *
 * ⚠️ **Il appartient à une équipe, jamais à une personne.** C'est la ligne de la grille du
 * planning (`Team`, [M22](../../../../docs/MODELE-DONNEES.md#m22)) : un point qui viserait un
 * agent n'aurait pas de ligne où s'afficher, et l'équipe de 2 est l'unité qui se déplace.
 *
 * ✅ **À une ou plusieurs équipes, qui partagent le même point** (#576) : un séminaire de cinq
 * équipes se modifie en un geste, pas en cinq. C'est une table de liaison,
 * `internal_meeting_team` ([M28](../../../../docs/MODELE-DONNEES.md#m28)).
 *
 * ⚠️ **Il a une durée, contrairement à un rendez-vous** — tranché le 22/09/2026 avec l'écran qui
 * la saisit ([M28](../../../../docs/MODELE-DONNEES.md#m28)). C'est le seul écart de forme entre les
 * deux natures de créneau, et il vient du prototype validé : la modale « Créer un point interne »
 * porte une molette « Durée », celle de prise de rendez-vous n'en a pas. Le motif métier tient en
 * une phrase — un rendez-vous dure ce que dure le contrôle, une réunion se cale sur une plage
 * décidée à l'avance. La colonne entre **avec** cet écran, pas « au cas où ».
 */
export const internalMeetingSchema = z.object({
    id: uuidV7Schema,
    /** Les équipes dont le créneau est occupé — chacune y voit le point sur sa ligne de la grille. */
    teamIds: z.array(uuidV7Schema).min(1),
    /** Ce qui s'affiche sur la carte : « Brief hebdo », « Point marché Mauges ». */
    title: z.string().trim().min(1),
    /**
     * Où ça se passe, **nullable** : le prototype ne colore que les points « sur site », mais une
     * réunion au bureau n'a pas de lieu à nommer. L'exiger ferait refuser une saisie parfaitement
     * sensée, ce qu'interdit [M21](../../../../docs/MODELE-DONNEES.md#m21).
     */
    place: z.string().trim().min(1).nullable(),
    /**
     * Instant du point, avec fuseau (M5) — **même colonne et même nom que `Appointment`**, parce
     * que les deux se rangent côte à côte dans la même cellule de la grille. Deux noms pour le
     * même concept obligeraient chaque lecture de la semaine à les réconcilier.
     */
    scheduledAt: z.date(),
    /**
     * Combien de temps le créneau occupe l'équipe, en minutes — le seul champ qu'un rendez-vous n'a
     * pas. Motif et bornes : `durationMinutesSchema` ci-dessus.
     */
    durationMinutes: durationMinutesSchema,
});
/**
 * Ce qu'un opérateur fournit pour poser un point interne. L'`id` n'y est pas : il est tiré côté
 * serveur (M7), comme pour un rendez-vous.
 *
 * ⚠️ **Le format est exigé avant la coercition**, exactement pour la raison écrite en tête de
 * `scheduleAppointmentSchema` : `z.coerce.date()` seul transforme `null`, `false` et `0` en 1ᵉʳ
 * janvier 1970 sans rien signaler, et un champ date resté vide poserait un point interne en 1970.
 * `z.iso.datetime({ offset: true })` exige d'abord la chaîne ISO que le JSON transporte vraiment.
 */
export const scheduleInternalMeetingSchema = z.object({
    /** Une équipe cochée deux fois n'est retenue qu'une fois : ce n'est pas une faute à refuser. */
    teamIds: z
        .array(uuidV7Schema)
        .min(1)
        .transform((ids) => [...new Set(ids)]),
    title: z.string().trim().min(1),
    place: z.string().trim().min(1).nullable(),
    scheduledAt: z.iso.datetime({ offset: true }).pipe(z.coerce.date()),
    /**
     * ⚠️ **Exigée, sans repli côté serveur.** Un défaut posé ici — 60, par exemple — enregistrerait
     * une durée que personne n'a choisie et qu'aucun écran ne montrerait fausse. C'est la modale qui
     * la pré-sélectionne (`INTERNAL_MEETING_DEFAULT_DURATION`), sous l'œil de l'opérateur.
     */
    durationMinutes: durationMinutesSchema,
});
/**
 * Ce que `POST /planning/internal-meetings` rend : le point tel qu'il passe **sur le fil**.
 *
 * ⚠️ **`scheduledAt` y est une chaîne ISO, alors qu'`internalMeetingSchema` exige une `Date`** — et
 * ce n'est pas une incohérence, c'est exactement la distinction que porte déjà le couple
 * `appointmentSchema` / `scheduledAppointmentSchema`. Celui-là décrit la **ligne en base**, où la
 * colonne est un `timestamptz` ; celui-ci décrit ce que `JSON.stringify` en fait. Le client d'API
 * valide la réponse contre ce schéma (D17) : lui donner `z.date()` ferait échouer toute réponse
 * pourtant correcte — c'est ce qu'un test du client a levé, et non une précaution théorique.
 *
 * `offset: true` accepte `…Z` comme `…+02:00` : les deux portent leur fuseau (M5).
 */
export const scheduledInternalMeetingSchema = internalMeetingSchema.extend({
    scheduledAt: z.iso.datetime({ offset: true }),
});
//# sourceMappingURL=internal-meeting.js.map